/**
 * Readable URLs and search titles for Watchtower posts.
 *
 * Posts live at /watchtower/{slug}; the old /watchtower/{uuid} URLs 301 to
 * the slug. Slugs are set once (on create / backfill) and never change when
 * a title is edited, so published links stay stable.
 */

const MAX_SLUG_LENGTH = 60;
const MAX_SEO_TITLE_LENGTH = 58;

// Words a slug or cut title should not end on ("…-in-a-row-and").
const TRAILING_STOPWORDS = new Set([
  "a", "an", "and", "are", "as", "at", "by", "for", "from", "in", "is", "its",
  "most", "of", "on", "or", "than", "that", "the", "their", "to", "with",
]);

const EMOJI_RE = /[\p{Extended_Pictographic}\p{Regional_Indicator}\u{FE0F}\u{200D}]/gu;

function dropTrailingStopwords(words: string[]): string[] {
  const out = [...words];
  while (out.length > 1 && TRAILING_STOPWORDS.has(out[out.length - 1].toLowerCase().replace(/[^a-z]/g, ""))) {
    out.pop();
  }
  return out;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isWatchtowerUuid(value: string): boolean {
  return UUID_RE.test(value);
}

/** "European semiconductors are not just $ASML." → "european-semiconductors-are-not-just-asml" */
export function slugifyWatchtowerTitle(title: string): string {
  const words = (title || "")
    .replace(/\*\*/g, "")
    .replace(/['’]/g, "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/%/g, " percent ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  const kept: string[] = [];
  for (const word of words) {
    if ([...kept, word].join("-").length > MAX_SLUG_LENGTH) break;
    kept.push(word);
  }
  return dropTrailingStopwords(kept).join("-") || "post";
}

/** Appends -2, -3… until the slug is not in `taken`. */
export function uniqueWatchtowerSlug(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base}-${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/**
 * Title for <title>/og: the hand-written seo_title when set, otherwise the
 * post's first sentence when it fits, otherwise the title cut at a word
 * boundary — so Google doesn't truncate it mid-word.
 */
export function watchtowerSeoTitle(post: { title: string; seo_title?: string | null }): string {
  const manual = post.seo_title?.trim();
  if (manual) return manual;
  const title = (post.title || "")
    .replace(/\*\*/g, "")
    .replace(EMOJI_RE, "")
    .replace(/\s+/g, " ")
    .trim();
  if (title.length <= MAX_SEO_TITLE_LENGTH) return title;
  const firstSentence = title.match(/^.+?[.!?](?=\s|$)/)?.[0];
  if (firstSentence && firstSentence.length >= 25 && firstSentence.length <= MAX_SEO_TITLE_LENGTH) {
    return firstSentence.replace(/\.$/, "");
  }
  const words = title.slice(0, MAX_SEO_TITLE_LENGTH + 1).split(" ");
  // Drop the last word only when the slice cut through it.
  if (title.length > MAX_SEO_TITLE_LENGTH + 1 && title[MAX_SEO_TITLE_LENGTH + 1] !== " ") words.pop();
  return dropTrailingStopwords(words).join(" ").replace(/[\s,;:.–—-]+$/, "");
}

export function watchtowerPostPath(post: { id: string; slug?: string | null }): string {
  return `/watchtower/${post.slug || post.id}`;
}
